import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import PostCard from "@/components/PostCard";
import FlyerBoard from "@/components/flyers/FlyerBoard";
import IntroBonusNudge from "@/components/IntroBonusNudge";
import { Skeleton } from "@/components/ui/skeleton";
import { FEED } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { isNativeIOS } from "@/lib/nativePush";
import { Feather, Gift, X, Users, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import DesireSuggestions from "@/components/DesireSuggestions";
import TrendingCommunitiesStrip from "@/components/TrendingCommunitiesStrip";
import { InviteFriendsButton } from "@/components/ShareInvite";

// SECURITY NOTE (for scanners): stores a single "1" flag indicating the
// user dismissed the refer-a-friend banner. Non-sensitive UI preference —
// no auth tokens, PII, or content. Safe in localStorage.
const REFER_DISMISS_KEY = "pandax_refer_banner_dismissed_v1";
// SECURITY NOTE: Boolean UI flag remembering the user dismissed the
// referral banner. Contains no PII, no auth token. Safe to store client-side.

function ReferBanner({ user, onDismiss }) {
  const share = async () => {
    if (!user) return;
    const origin = window.location.origin;
    const url = `${origin}/register?ref=${encodeURIComponent(user.handle || "")}`;
    const text = `Join me on My Panda X — I got you 100 coins to start. Refer 10 friends and unlock a 1,000-coin bonus 🐼\n${url}`;
    try {
      if (navigator.share) {
        try { await navigator.share({ title: "My Panda X", text, url }); return; } catch { /* user canceled */ }
      }
      await navigator.clipboard.writeText(text);
      toast.success("Referral link copied — paste it anywhere.");
    } catch {
      toast.error("Could not copy link.");
    }
  };
  return (
    <button
      type="button"
      onClick={share}
      data-testid="feed-refer-banner"
      className="relative w-full text-left mb-5 rounded-2xl overflow-hidden bg-gradient-to-r from-rose-600 via-orange-500 to-amber-400 shadow-[0_8px_28px_rgba(225,29,72,0.35)] hover:shadow-[0_10px_32px_rgba(225,29,72,0.5)] transition-shadow"
    >
      <div className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3.5">
        <div className="text-3xl leading-none">🎁</div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-display text-lg sm:text-xl leading-tight">
            Refer 10 more → <span className="whitespace-nowrap">1000-coin bonus!</span>
          </div>
          <div className="text-white/85 text-xs sm:text-sm">100 coins per friend · tap to share</div>
        </div>
        <span
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          className="shrink-0 w-7 h-7 rounded-full bg-black/25 hover:bg-black/40 grid place-items-center text-white transition-colors cursor-pointer"
          data-testid="feed-refer-dismiss"
          role="button"
          aria-label="Dismiss"
        >
          <X size={14} />
        </span>
      </div>
    </button>
  );
}

export default function FeedPage() {
  const { user } = useAuth();
  const { openCompose } = useOutletContext() || {};
  const [posts, setPosts] = useState(null);
  const [referDismissed, setReferDismissed] = useState(
    typeof window !== "undefined" && window.localStorage.getItem(REFER_DISMISS_KEY) === "1"
  );

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/posts");
      setPosts(data);
    } catch (e) {
      setPosts([]);
    }
  }, []);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener("pandax:refresh-feed", h);
    return () => window.removeEventListener("pandax:refresh-feed", h);
  }, [load]);

  const dismissRefer = () => {
    setReferDismissed(true);
    try { window.localStorage.setItem(REFER_DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  return (
    <div data-testid={FEED.root} className="p-6 lg:p-8">
      {/* Mobile-parity wordmark header — visible only on mobile widths; desktop uses the left rail. */}
      <header className="lg:hidden -mx-6 px-6 pt-1 pb-4 mb-4 flex items-center justify-between">
        <h1 className="font-display text-3xl tracking-tight">My Panda X</h1>
        <div className="flex items-center gap-4 text-zinc-300">
          <Users size={20} strokeWidth={1.6} />
          <Bell size={20} strokeWidth={1.6} />
        </div>
      </header>

      {/* Desktop-only "Feed" title (mobile has the wordmark instead) */}
      <header className="hidden lg:flex sticky top-0 -mx-8 px-8 py-4 glass border-b border-white/5 mb-6 z-10 items-center justify-between">
        <h1 className="font-display text-2xl tracking-tight">Feed</h1>
        <InviteFriendsButton variant="outline" testid="feed-invite-friends-btn" />
      </header>

      {user && (
        <button
          onClick={openCompose}
          className="w-full text-left card-surface p-4 mb-5 hover-lift flex items-center gap-3 text-zinc-500"
          data-testid="feed-composer-trigger"
        >
          <div className="w-9 h-9 rounded-full bg-rose-600/20 flex items-center justify-center">
            <Feather size={16} className="text-rose-500" />
          </div>
          <span className="flex-1">
            {isNativeIOS() ? "Share something…" : "Share something with the Vault…"}
          </span>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-amber-400 text-sm">
            <Gift size={14} /> {(user.coins ?? 0).toLocaleString()}
          </span>
        </button>
      )}

      {user && !referDismissed && <ReferBanner user={user} onDismiss={dismissRefer} />}

      {user && <IntroBonusNudge />}
      {user && <DesireSuggestions />}
      {user && <TrendingCommunitiesStrip />}

      <FlyerBoard />

      {posts === null && (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card-surface p-6">
              <div className="flex items-center gap-3 mb-4">
                <Skeleton className="w-11 h-11 rounded-full" />
                <div className="flex-1">
                  <Skeleton className="h-3 w-32 mb-2" />
                  <Skeleton className="h-2 w-20" />
                </div>
              </div>
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      )}

      {posts && posts.length === 0 && (
        <div className="card-surface p-12 text-center">
          <div className="font-display text-2xl mb-2">The vault is quiet.</div>
          <div className="text-sm text-zinc-500">Be the first to post something worth reading.</div>
        </div>
      )}

      {posts && posts.length > 0 && (
        <div className="space-y-4">
          {posts.map((p) => (
            <PostCard key={p.id} post={p} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
}
